import React, { useState, useMemo, useCallback, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";

import Tooltip from "@mui/material/Tooltip";
import {
  format,
  parse,
  subDays,
  addDays,
  startOfDay,
  startOfMonth,
  endOfMonth,
} from "date-fns";

import {
  CalendarToday as CalendarIcon,
  FolderOpen as FolderOpenIcon,
  Folder as FolderIcon,
  MoreHoriz as MoreHorizIcon,
} from "@mui/icons-material";

import { Close as CloseIcon } from "@mui/icons-material";

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { GoogleDriveFileList } from "../GoogleDriveFileList";
import {
  getDrawingPathBreadcrumbSegments,
  DRAWING_PATH_SEPARATOR_COLOR,
} from "../../../utils/drawingPathBreadcrumb.js";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { DatePickerModal } from "../DatePickerModal";
import { post } from "../../../services/api";

/**
 * Calculate date range for a given date option
 * @param {string} option - The date filter option
 * @returns {{fromDate: string, toDate: string}} - Date range in YYYY-MM-DD format
 */
const getDateRange = (option) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let fromDate, toDate;

  switch (option) {
    case "Today": {
      fromDate = new Date(today);
      toDate = new Date(today);
      break;
    }
    case "Yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      fromDate = new Date(yesterday);
      toDate = new Date(yesterday);
      break;
    }
    case "Last 7 Days": {
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);
      fromDate = new Date(sevenDaysAgo);
      toDate = new Date(today);
      break;
    }
    case "Last 30 Days": {
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);
      fromDate = new Date(thirtyDaysAgo);
      toDate = new Date(today);
      break;
    }
    case "This Week": {
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday is start of week
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() + diff);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      fromDate = new Date(startOfWeek);
      toDate = new Date(endOfWeek);
      break;
    }
    case "This Month": {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      fromDate = new Date(startOfMonth);
      toDate = new Date(endOfMonth);
      break;
    }
    default: {
      fromDate = new Date(today);
      toDate = new Date(today);
    }
  }

  // Format dates as YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return {
    fromDate: formatDate(fromDate),
    toDate: formatDate(toDate),
  };
};

export function PatrolListHeader({
  selectedDrawingForView,
  onSelectDrawing,
  onClearSelectedDrawing,
  totalCount = 0,
  currentCount = 0,
  dateFilter = "Today",
  fromDate,
  toDate,
  onDateFilterChange,
  onSettingsClick,
  isSettingsOpen = false,
}) {
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [isCustomRangeModalOpen, setIsCustomRangeModalOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [drawingPickerAnchorEl, setDrawingPickerAnchorEl] = useState(null);
  const [drawingPathEllipsisAnchor, setDrawingPathEllipsisAnchor] =
    useState(null);

  const drawingPathBreadcrumb = useMemo(
    () => getDrawingPathBreadcrumbSegments(selectedDrawingForView),
    [selectedDrawingForView],
  );

  const drawingPathTooltipTitle =
    selectedDrawingForView?.directory_path ||
    selectedDrawingForView?.path ||
    selectedDrawingForView?.name ||
    "";

  // Convert dd/mm/yyyy to YYYY-MM-DD for date input
  const convertToDateInputFormat = (dateString) => {
    if (!dateString) return "";
    try {
      const parsed = parse(dateString, "dd/MM/yyyy", new Date());
      if (isNaN(parsed.getTime())) return "";
      return format(parsed, "yyyy-MM-dd");
    } catch {
      return "";
    }
  };

  // Convert YYYY-MM-DD from date input to dd/mm/yyyy
  const convertFromDateInputFormat = (dateString) => {
    if (!dateString) return "";
    try {
      const parsed = parse(dateString, "yyyy-MM-dd", new Date());
      if (isNaN(parsed.getTime())) return "";
      return format(parsed, "dd/MM/yyyy");
    } catch {
      return "";
    }
  };

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    if (fromDate) {
      const d = new Date(`${fromDate}T00:00:00`);
      return isNaN(d.getTime()) ? new Date() : d;
    }
    return new Date();
  });
  const [calendarDates, setCalendarDates] = useState([]);
  const [viewingMonth, setViewingMonth] = useState(new Date());

  useEffect(() => {
    if (!fromDate) return;
    const d = new Date(`${fromDate}T00:00:00`);
    if (!isNaN(d.getTime())) setSelectedDate(d);
  }, [fromDate]);

  useEffect(() => {
    const fetchPatrolCalendarDates = async () => {
      try {
        const monthStart = startOfMonth(viewingMonth);
        const monthEnd = endOfMonth(viewingMonth);
        const response = await post("/patrol", {
          from: format(monthStart, "yyyy-MM-dd"),
          to: format(monthEnd, "yyyy-MM-dd"),
          querytype: "list",
        });

        const data = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

        const seen = new Set();
        const uniqueDates = data
          .filter((p) => p?.created_at)
          .reduce((acc, p) => {
            const pd = new Date(p.created_at);
            if (isNaN(pd.getTime())) return acc;
            const key = format(pd, "yyyy-MM-dd");
            if (!seen.has(key)) {
              seen.add(key);
              acc.push(pd);
            }
            return acc;
          }, []);

        setCalendarDates(uniqueDates);
      } catch {
        setCalendarDates([]);
      }
    };

    fetchPatrolCalendarDates();
  }, [viewingMonth]);

  const handleSingleDayCalendarSelect = (date) => {
    setSelectedDate(date);
    const apiFrom = format(date, "yyyy-MM-dd");
    onDateFilterChange?.(format(date, "MMM d, yyyy"), apiFrom, apiFrom);
  };

  const handleDateMenuOpen = (event) => {
    setDateMenuAnchor(event.currentTarget);
  };

  const handleDateMenuClose = () => {
    setDateMenuAnchor(null);
  };

  const handleDrawingChipClick = useCallback(
    (event) => {
      if (!onSelectDrawing) return;
      const target = event.currentTarget;
      setDrawingPickerAnchorEl((prev) => (prev === target ? null : target));
    },
    [onSelectDrawing],
  );

  const closeDrawingPicker = () => {
    setDrawingPickerAnchorEl(null);
  };

  const handlePickerFileSelect = (node) => {
    if (node?.type === "file" && onSelectDrawing) {
      onSelectDrawing(node);
      closeDrawingPicker();
    }
  };

  // Format date to dd/mm/yyyy for display
  const formatDateToDisplay = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Parse dd/mm/yyyy to Date object
  const parseDateFromDisplay = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split("/");
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const year = parseInt(parts[2], 10);
    const date = new Date(year, month, day);
    // Validate date
    if (
      date.getDate() !== day ||
      date.getMonth() !== month ||
      date.getFullYear() !== year
    ) {
      return null;
    }
    return date;
  };

  // Format date to YYYY-MM-DD for API
  const formatDateToAPI = (date) => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handlePresetDateFilter = (value) => {
    if (value === "Custom Range...") {
      // Open custom range modal
      // If custom range is already selected, restore the previous dates
      if (dateFilter === "Custom Range..." && fromDate && toDate) {
        // Convert fromDate and toDate (which are in YYYY-MM-DD format) to date input format
        setTempStartDate(fromDate);
        setTempEndDate(toDate);
      } else {
        // Otherwise, set default dates (last week to today)
        const today = new Date();
        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 7);

        // Set dates in YYYY-MM-DD format for date inputs
        setTempStartDate(format(lastWeek, "yyyy-MM-dd"));
        setTempEndDate(format(today, "yyyy-MM-dd"));
      }
      setIsCustomRangeModalOpen(true);
      handleDateMenuClose();
    } else {
      // Calculate date range for the selected option
      const { fromDate, toDate } = getDateRange(value);
      onDateFilterChange?.(value, fromDate, toDate);
      handleDateMenuClose();
    }
  };

  const handleApplyCustomRange = () => {
    if (tempStartDate && tempEndDate) {
      // tempStartDate and tempEndDate are in YYYY-MM-DD format from date inputs
      const startDate = parse(tempStartDate, "yyyy-MM-dd", new Date());
      const endDate = parse(tempEndDate, "yyyy-MM-dd", new Date());

      // Validate dates
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        alert("Please select valid dates");
        return;
      }

      if (startDate > endDate) {
        alert("Start date must be before end date");
        return;
      }

      // Format dates as YYYY-MM-DD for API
      onDateFilterChange?.(
        "Custom Range...",
        format(startDate, "yyyy-MM-dd"),
        format(endDate, "yyyy-MM-dd"),
      );
      setIsCustomRangeModalOpen(false);
      setTempStartDate("");
      setTempEndDate("");
    }
  };

  // Handle date input change - date inputs use YYYY-MM-DD format
  const handleStartDateChange = (e) => {
    setTempStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setTempEndDate(e.target.value);
  };

  const handleCancelCustomRange = () => {
    setIsCustomRangeModalOpen(false);
    setTempStartDate("");
    setTempEndDate("");
  };

  const handleResetToToday = (event) => {
    event.stopPropagation();
    const { fromDate: todayFromDate, toDate: todayToDate } =
      getDateRange("Today");
    onDateFilterChange?.("Today", todayFromDate, todayToDate);
  };

  // Get tooltip text for custom date range
  const getTooltipText = () => {
    if (dateFilter === "Custom Range..." && fromDate && toDate) {
      const formattedFromDate = formatDateToDisplay(fromDate);
      const formattedToDate = formatDateToDisplay(toDate);
      return `${formattedFromDate} - ${formattedToDate}`;
    }
    return dateFilter;
  };

  const isNotToday = dateFilter !== "Today";
  const tooltipText = getTooltipText();

  const dateOptions = [
    "Today",
    "Yesterday",
    "Last 7 Days",
    "Last 30 Days",
    "This Week",
    "This Month",
    "Custom Range...",
  ];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        py: 2,
        bgcolor: "#F9FAFB",
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}
      >
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "16px",
            fontWeight: 400,
            color: "#FF8555",
          }}
        >
          Patrol List
        </Typography>
        {selectedDrawingForView?.id ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0,
                maxWidth: "min(100%, 560px)",
              }}
            >
              <Tooltip
                title={drawingPathTooltipTitle}
                placement="bottom-start"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontWeight: 600,
                      maxWidth: 480,
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    minWidth: 0,
                    flexShrink: 1,
                  }}
                >
                  {drawingPathBreadcrumb.hiddenFolders.length > 0 && (
                    <>
                      <IconButton
                        size="small"
                        aria-label="Show folders in path"
                        aria-haspopup="true"
                        aria-expanded={Boolean(drawingPathEllipsisAnchor)}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDrawingPathEllipsisAnchor(e.currentTarget);
                        }}
                        sx={{
                          color: "#4A5565",
                          p: 0.5,
                          flexShrink: 0,
                          "&:hover": {
                            bgcolor: "rgba(41, 50, 229, 0.08)",
                          },
                        }}
                      >
                        <MoreHorizIcon sx={{ fontSize: 22 }} />
                      </IconButton>
                      <Typography
                        component="span"
                        sx={{
                          color: DRAWING_PATH_SEPARATOR_COLOR,
                          fontSize: "16px",
                          lineHeight: 1,
                          flexShrink: 0,
                          fontWeight: 500,
                        }}
                        aria-hidden
                      >
                        ›
                      </Typography>
                    </>
                  )}
                  {drawingPathBreadcrumb.parentFolder && (
                    <>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "13px",
                          fontFamily: "Roboto, sans-serif",
                          fontWeight: 500,
                          color: "#4a5565",
                          padding: "4px 6px",
                          borderRadius: "4px",
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "200px",
                          flexShrink: 1,
                        }}
                      >
                        {drawingPathBreadcrumb.parentFolder}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          color: DRAWING_PATH_SEPARATOR_COLOR,
                          fontSize: "16px",
                          lineHeight: 1,
                          flexShrink: 0,
                          fontWeight: 500,
                        }}
                        aria-hidden
                      >
                        ›
                      </Typography>
                    </>
                  )}
                </Box>
              </Tooltip>
              <Tooltip
                title={
                  onSelectDrawing
                    ? "Click to choose another drawing"
                    : `Filtered by: ${drawingPathTooltipTitle}`
                }
                placement="bottom-start"
                componentsProps={{
                  tooltip: {
                    sx: { fontWeight: 600 },
                  },
                }}
              >
                <Chip
                  icon={<FolderOpenIcon />}
                  label={selectedDrawingForView.name}
                  size="small"
                  clickable={Boolean(onSelectDrawing)}
                  onClick={
                    onSelectDrawing ? handleDrawingChipClick : undefined
                  }
                  sx={{
                    bgcolor: "#2932E5",
                    color: "white",
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 600,
                    height: "26px",
                    fontSize: "13px",
                    flexShrink: 0,
                    maxWidth: "100%",
                    "& .MuiChip-icon": { color: "white" },
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                    "&.MuiChip-clickable:hover": {
                      bgcolor: "#2932E5",
                    },
                    "&.MuiChip-clickable:active": {
                      bgcolor: "#2932E5",
                    },
                    ...(onSelectDrawing
                      ? {
                          cursor: "pointer",
                          "&:focus-visible": {
                            boxShadow: "0 0 0 2px rgba(41, 50, 229, 0.45)",
                          },
                        }
                      : {}),
                  }}
                />
              </Tooltip>
            </Box>
            <Menu
              id="patrol-drawing-path-menu"
              anchorEl={drawingPathEllipsisAnchor}
              open={Boolean(drawingPathEllipsisAnchor)}
              onClose={() => setDrawingPathEllipsisAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: "12px",
                    minWidth: 220,
                    mt: 0.5,
                    boxShadow: "0px 4px 24px rgba(15, 23, 42, 0.12)",
                  },
                },
              }}
              MenuListProps={{ dense: true }}
            >
              {drawingPathBreadcrumb.hiddenFolders.map((folderName, idx) => (
                <MenuItem
                  key={`${folderName}-${idx}`}
                  onClick={() => setDrawingPathEllipsisAnchor(null)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <FolderIcon
                      fontSize="small"
                      sx={{ color: "#5F6368" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={folderName}
                    primaryTypographyProps={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      noWrap: true,
                    }}
                  />
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : null}
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "14px",
            color: "#99A1AF",
            marginLeft: "8px",
          }}
        >
          {currentCount ? currentCount : 0} of {totalCount ? totalCount : 0}{" "}
          patrols
        </Typography>
        {/* {onSettingsClick && (
          <Tooltip title="Patrol Settings">
            <IconButton
              onClick={onSettingsClick}
              size="small"
              sx={{
                ml: 1,
                color: isSettingsOpen ? "#2932E5" : "#4A5565",
                bgcolor: isSettingsOpen
                  ? "rgba(41, 50, 229, 0.08)"
                  : "transparent",
                opacity: isSettingsOpen ? 1 : 0.7,
                width: 40,
                height: 40,
                transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  color: "#2932E5",
                  bgcolor: "rgba(41, 50, 229, 0.08)",
                  opacity: 1,
                },
              }}
            >
              <Badge
                variant="dot"
                invisible={!isSettingsOpen}
                sx={{
                  "& .MuiBadge-dot": {
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "#2932E5",
                  },
                }}
              >
                <SettingsOutlinedIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        )} */}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}
      >
      {/* Date Filter Dropdown */}
      <Tooltip
        title={tooltipText || ""}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: "black",
              color: "white",
              "& .MuiTooltip-arrow": {
                color: "black",
              },
            },
          },
        }}
        disableHoverListener={!tooltipText}
      >
        <Button
          variant="outlined"
          onClick={handleDateMenuOpen}
          sx={{
            textTransform: "none",
            fontFamily: "Roboto, sans-serif",
            fontSize: "14px",
            color: "#4A5565",
            fontWeight: 400,
            fontStyle: "bold",
            borderColor: dateMenuAnchor ? "#2932e5" : "#E5E7EB",
            borderWidth: dateMenuAnchor ? "1px" : "1px",
            borderRadius: "10px",
            width: "180px",
            px: 0.5,
            py: 0.75,
            bgcolor: "white",
            position: "relative",
            "&:hover": {
              borderColor: "#2932e5",
              borderWidth: "1px",
              bgcolor: "white",
            },
            "&:focus": {
              borderColor: "#2932e5",
              borderWidth: "1px",
              bgcolor: "white",
            },

            "& .MuiButton-startIcon": {
              position: "absolute",
              left: "12px",
              margin: 0,
            },
            "& .MuiButton-endIcon": {
              position: "absolute",
              right: "12px",
              margin: 0,
            },
          }}
        >
          <CalendarIcon
            sx={{ fontSize: 18, position: "absolute", left: "12px" }}
          />
          <Box
            sx={{
              flex: 1,
              textAlign: "start",
              marginLeft: "4px",
              px: 3,
            }}
          >
            {dateFilter}
          </Box>
          {isNotToday ? (
            <Box
              component="span"
              onClick={handleResetToToday}
              sx={{
                position: "absolute",
                right: "12px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                borderRadius: "50%",
                width: 20,
                height: 20,
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </Box>
          ) : (
            <Box
              sx={{
                position: "absolute",
                right: "12px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {dateMenuAnchor ? (
                <ArrowDropUpIcon sx={{ fontSize: 16 }} />
              ) : (
                <ArrowDropDownIcon sx={{ fontSize: 16 }} />
              )}
            </Box>
          )}
        </Button>
      </Tooltip>
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={handleDateMenuClose}
        disableScrollLock={true}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: "180px",
            boxShadow: "10px 4px 6px rgba(0, 0, 0, 0.1)",
            borderRadius: "10px",
          },
        }}
      >
        {dateOptions.map((option) => (
          <MenuItem
            key={option}
            onClick={() => handlePresetDateFilter(option)}
            selected={option === dateFilter}
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "black",
              "&.Mui-selected": {
                bgcolor: "rgba(41, 50, 229, 0.08)",
                color: "#2932E5",
                "&:hover": {
                  bgcolor: "rgba(41, 50, 229, 0.12)",
                },
              },
              "&:hover": {
                bgcolor: "#F3F4F6",
              },
            }}
          >
            {option}
          </MenuItem>
        ))}
      </Menu>

      {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title="Previous Date" arrow>
          <IconButton
            onClick={() =>
              handleSingleDayCalendarSelect(subDays(selectedDate, 1))
            }
            sx={{
              bgcolor: "white",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              width: "36px",
              height: "36px",
              "&:hover": {
                bgcolor: "#FFE0D0",
                borderColor: "#FF8555",
              },
            }}
          >
            <ChevronLeft size={18} />
          </IconButton>
        </Tooltip>

        <Tooltip
          title={format(selectedDate, "MMM d, yyyy")}
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "black",
                color: "white",
                "& .MuiTooltip-arrow": { color: "black" },
              },
            },
          }}
        >
          <Button
            onClick={() => setShowDatePicker(true)}
            sx={{
              px: 1.5,
              py: 0.75,
              bgcolor: "white",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: "auto",
              height: "36px",
              "&:hover": {
                bgcolor: "#FFE0D0",
                borderColor: "#FF8555",
              },
            }}
          >
            <Calendar size={14} color="#2932E5" />
            <Typography
              noWrap
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                fontWeight: 600,
                color: "#2932E5",
                textTransform: "none",
              }}
            >
              {format(selectedDate, "MMM d")}
            </Typography>
          </Button>
        </Tooltip>

        <Tooltip title="Next Date" arrow>
          <IconButton
            onClick={() =>
              handleSingleDayCalendarSelect(addDays(selectedDate, 1))
            }
            disabled={startOfDay(selectedDate) >= startOfDay(new Date())}
            sx={{
              bgcolor: "white",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              width: "36px",
              height: "36px",
              "&:hover": {
                bgcolor: "#FFE0D0",
                borderColor: "#FF8555",
              },
              "&:disabled": {
                bgcolor: "#f9fafb",
                opacity: 0.5,
              },
            }}
          >
            <ChevronRight size={18} />
          </IconButton>
        </Tooltip>
      </Box> */}
      </Box>

      {onSelectDrawing ? (
        <Popover
          open={Boolean(drawingPickerAnchorEl)}
          anchorEl={drawingPickerAnchorEl}
          onClose={closeDrawingPicker}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              elevation: 8,
              sx: {
                mt: 0.75,
                width: "min(560px, calc(100vw - 24px))",
                maxWidth: "100%",
                maxHeight: "min(70vh, 580px)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                borderRadius: 2,
                minHeight: 0,
              },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              px: 1.5,
              py: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Choose drawing
            </Typography>
            <IconButton
              size="small"
              onClick={closeDrawingPicker}
              aria-label="Close drawing picker"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <GoogleDriveFileList
              drawingPickerMode
              onFileSelect={handlePickerFileSelect}
              selectedDrawingForView={selectedDrawingForView}
              onClearSelectedDrawing={onClearSelectedDrawing}
            />
          </Box>
        </Popover>
      ) : null}

      {/* Custom Date Range Modal */}
      <Dialog
        open={isCustomRangeModalOpen}
        onClose={handleCancelCustomRange}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "18px",
            fontWeight: 600,
            color: "#212121",
            pb: 1,
          }}
        >
          Select Custom Date Range
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              mt: 2,
            }}
          >
            <Box sx={{ position: "relative", width: "100%" }}>
              <TextField
                variant="outlined"
                type="date"
                value={tempStartDate}
                onChange={handleStartDateChange}
                fullWidth
                size="medium"
                helperText="Start Date"
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  max: tempEndDate || undefined,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: 0,
                    borderRadius: "10px",
                    "& fieldset": {
                      borderRadius: "10px",
                    },
                  },
                  "& input[type='date']": {
                    color: "black",
                    fontSize: "14px",
                    "&::-webkit-calendar-picker-indicator": {
                      cursor: "pointer",
                      zIndex: 1,
                    },
                  },
                }}
                FormHelperTextProps={{
                  sx: {
                    mt: 0,
                    mx: 0,
                  },
                }}
              />
              {/* {tempStartDate && (
                <Box
                  sx={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    color: "rgba(0, 0, 0, 0.87)",
                    fontSize: "1rem",
                    zIndex: 0,
                  }}
                >
                  {convertFromDateInputFormat(tempStartDate)}
                </Box>
              )} */}
            </Box>

            <Box sx={{ position: "relative", width: "100%" }}>
              <TextField
                variant="outlined"
                type="date"
                value={tempEndDate}
                onChange={handleEndDateChange}
                fullWidth
                size="medium"
                helperText="End Date"
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: tempStartDate || undefined,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: 0,
                    borderRadius: "10px",
                    "& fieldset": {
                      borderRadius: "10px",
                    },
                  },
                  "& input[type='date']": {
                    color: "black",
                    fontSize: "14px",
                    "&::-webkit-calendar-picker-indicator": {
                      cursor: "pointer",
                      zIndex: 1,
                    },
                  },
                }}
                FormHelperTextProps={{
                  sx: {
                    mt: 0,
                    mx: 0,
                  },
                }}
              />
              {/* {tempEndDate && (
                <Box
                  sx={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    color: "rgba(0, 0, 0, 0.87)",
                    fontSize: "1rem",
                    zIndex: 0,
                  }}
                >
                  {convertFromDateInputFormat(tempEndDate)}
                </Box>
              )} */}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2,
          }}
        >
          <Button
            onClick={handleCancelCustomRange}
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#4a5565",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApplyCustomRange}
            variant="contained"
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              borderRadius: "10px",
              bgcolor: "#2932E5",
              "&:hover": {
                bgcolor: "#1e24b8",
              },
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <DatePickerModal
        open={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        selectedDate={selectedDate}
        onDateSelect={handleSingleDayCalendarSelect}
        latestDate={new Date()}
        availableDates={calendarDates}
        onViewingMonthChange={setViewingMonth}
      />
    </Box>
  );
}
